import {
  DOCUMENT,
  EnvironmentInjector,
  Service,
  computed,
  createEnvironmentInjector,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { AgentConversationStore, ConversationEntry } from 'app/shared/agent/agent-conversation.store';
import { FormaAttentionVariant } from 'forma';

/** Must match `AgentKeys.Coach` on the backend. */
export const COACH_AGENT_KEY = 'CoachAgent';

/** The conversation is long-lived on the server; the window only shows its tail. */
const VISIBLE_MESSAGES = 6;
/** The activity the coach proposes to create, as it will be saved (completed by the server). */
export interface ProposedAction {
  title: string;
  description: string | null;
  areaName: string | null;
  deadline: string | null;
}

export interface CoachContext {
  companyId: number;
  userId: number;
  groupId: number;
  productContextId: number | null;
  pageKey: string | null;
  /** The report pages the menu shows (its conditions already evaluated). */
  visiblePageKeys?: string[];
}

export interface CoachOrigin {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type CoachLayout = 'right' | 'bottom' | 'floating';

export interface CoachBounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface CoachDockSize {
  rightWidth: number | null;
  bottomLeft: number | null;
  bottomWidth: number | null;
  bottomHeight: number | null;
}

/**
 * Window state only. The conversation itself lives on the server (one Coach
 * session per company and user) and is loaded from there.
 */
interface CoachState {
  active: boolean;
  minimized: boolean;
  draft: string;
  layout: CoachLayout | null;
  bounds: CoachBounds | null;
  dockSize: CoachDockSize;
  /** A trigger has called for attention once in this browser session. */
  welcomed: boolean;
}

const STORAGE_PREFIX = 'insight.coach.v4:';
const emptyState = (): CoachState => ({
  active: false,
  minimized: false,
  draft: '',
  layout: null,
  bounds: null,
  dockSize: {
    rightWidth: null,
    bottomLeft: null,
    bottomWidth: null,
    bottomHeight: null,
  },
  welcomed: false,
});

@Service()
export class CoachStore {
  private readonly document = inject(DOCUMENT);
  // AgentConversationStore is provided per chat surface; the Coach has exactly one,
  // which outlives every page, so it gets its own injector under the root one.
  private readonly conversation = createEnvironmentInjector(
    [AgentConversationStore],
    inject(EnvironmentInjector),
  ).get(AgentConversationStore);

  private readonly context = signal<CoachContext | null>(null);
  private readonly state = signal<CoachState>(emptyState());
  private readonly launchOrigin = signal<CoachOrigin | null>(null);
  private readonly pendingFocus = signal(false);
  private readonly starting = signal(false);
  private readonly failed = signal(false);
  /** In memory only: a reload brings the window back at its normal size. */
  private readonly maximizedState = signal(false);
  private readonly attentionState = signal<FormaAttentionVariant | null>(null);
  private readonly minimizedAttentionState = signal<FormaAttentionVariant | null>(null);
  private sessionStart: Promise<void> | null = null;
  /** The company the shared hub connection was opened for; it can't change company. */
  private connectedCompanyId: number | null = null;
  /** A disconnect that must finish before the Coach connects again. */
  private reconnecting: Promise<void> = Promise.resolve();

  readonly available = computed(() => this.context() !== null);
  readonly active = computed(() => this.state().active);
  readonly minimized = computed(() => this.state().minimized);
  readonly maximized = this.maximizedState.asReadonly();
  readonly draft = computed(() => this.state().draft);
  readonly busy = computed(() => this.starting() || this.conversation.busy());
  /** A proposal (e.g. a new activity) the user must approve before anything is created. */
  readonly pendingConfirmation = this.conversation.pendingConfirmation;
  /** The details of a pending ProposeAction, so the card shows what will be created. */
  readonly proposedAction = computed<ProposedAction | null>(() => {
    const pending = this.pendingConfirmation();
    if (pending?.toolName !== 'ProposeAction') return null;
    try {
      const payload = JSON.parse(pending.proposedPayloadJson) as Partial<
        Record<'title' | 'description' | 'areaName' | 'deadline', string | null>
      >;
      return {
        title: payload.title?.trim() ?? '',
        description: payload.description?.trim() || null,
        areaName: payload.areaName?.trim() || null,
        deadline: payload.deadline || null,
      };
    } catch {
      return null;
    }
  });

  /** The Coach could not be started; the user's message is back in the composer. */
  readonly unavailable = this.failed.asReadonly();
  /**
   * The latest messages, with the context separators between them (and the one just above the
   * oldest message shown); separators don't count towards the number of messages.
   */
  readonly messages = computed<ConversationEntry[]>(() => {
    const entries = this.conversation
      .messages()
      .filter((entry) => ['user', 'assistant', 'error', 'context'].includes(entry.kind));
    let start = entries.length;
    let shown = 0;
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].kind !== 'context') {
        if (shown === VISIBLE_MESSAGES) break;
        shown++;
      }
      start = i;
    }
    return entries.slice(start);
  });
  readonly origin = this.launchOrigin.asReadonly();
  readonly focusRequested = this.pendingFocus.asReadonly();
  /** The trigger calls for attention while this is set. */
  readonly attention = this.attentionState.asReadonly();
  /** The minimized Coach calls for attention right after it is minimized. */
  readonly minimizedAttention = this.minimizedAttentionState.asReadonly();

  callForAttention(variant: FormaAttentionVariant = 'pine'): void {
    this.attentionState.set(variant);
  }

  finishAttention(): void {
    this.attentionState.set(null);
  }

  finishMinimizedAttention(): void {
    this.minimizedAttentionState.set(null);
  }

  /** No conversation is open and no trigger has welcomed the user in this browser session. */
  readonly welcomePending = computed(() => !this.active() && !this.state().welcomed);

  /** The first trigger to come into view calls for attention once per browser session. */
  welcome(): void {
    if (!this.welcomePending()) return;
    this.update({ welcomed: true });
    this.callForAttention('iris');
  }

  /**
   * The group, page and product only change what is sent with the next message.
   * A different company or user is a different conversation.
   */
  setContext(context: CoachContext | null): void {
    // Called from effects: reading and writing our own signals must not make the caller depend on them.
    untracked(() => this.applyContext(context));
  }

  private applyContext(context: CoachContext | null): void {
    const previous = this.context();
    if (JSON.stringify(previous) === JSON.stringify(context)) return;
    this.context.set(context);
    if (previous?.companyId === context?.companyId && previous?.userId === context?.userId) return;

    this.launchOrigin.set(null);
    this.pendingFocus.set(false);
    this.state.set(emptyState());
    this.sessionStart = null;
    this.failed.set(false);
    this.maximizedState.set(false);
    this.minimizedAttentionState.set(null);
    this.conversation.resetSession();
    // The hub connection is shared with other agent surfaces (Story), so it is only
    // closed when it was opened for another company and must be reopened for this one.
    if (context && this.connectedCompanyId !== null && this.connectedCompanyId !== context.companyId) {
      this.connectedCompanyId = null;
      this.reconnecting = this.conversation.disconnect().catch(() => undefined);
    }
    this.restoreState(context);
    if (this.active()) void this.ensureSession();
  }

  setDraft(draft: string): void {
    this.update({ draft });
  }

  readonly layout = computed(() => this.state().layout);
  readonly bounds = computed(() => this.state().bounds);
  readonly dockSize = computed(() => this.state().dockSize);

  send(text: string, origin?: CoachOrigin): boolean {
    const message = text.trim();
    if (!this.available() || !message || this.busy()) return false;
    if (!this.active() && origin) this.launchOrigin.set(origin);
    this.failed.set(false);
    this.update({ active: true, minimized: false, draft: '' });
    void this.deliver(message);
    return true;
  }

  approve(): void {
    void this.conversation.approve();
  }

  reject(feedback: string): void {
    void this.conversation.reject(feedback);
  }

  /** Starts over: the coach forgets the conversation so far. The draft is kept. */
  async newConversation(): Promise<void> {
    this.failed.set(false);
    await this.conversation.resetConversation();
  }

  minimize(): void {
    this.finishAnimation();
    this.minimizedAttentionState.set('pine');
    this.update({ minimized: true });
  }

  expand(): void {
    this.finishMinimizedAttention();
    this.update({ minimized: false });
  }

  toggleMaximized(): void {
    this.maximizedState.update((maximized) => !maximized);
  }

  dock(layout: Exclude<CoachLayout, 'floating'>): void {
    this.maximizedState.set(false);
    // Docking right again fits the window to the free space instead of a width dragged earlier.
    this.update({
      layout,
      bounds: null,
      ...(layout === 'right' ? { dockSize: { ...this.state().dockSize, rightWidth: null } } : {}),
    });
  }

  move(bounds: CoachBounds): void {
    this.maximizedState.set(false);
    this.update({ layout: 'floating', bounds });
  }

  resizeRightDock(width: number): void {
    this.update({
      dockSize: {
        ...this.state().dockSize,
        rightWidth: width,
      },
    });
  }

  resizeBottomDock(left: number, width: number, height = this.state().dockSize.bottomHeight): void {
    this.update({
      dockSize: {
        ...this.state().dockSize,
        bottomLeft: left,
        bottomWidth: width,
        bottomHeight: height,
      },
    });
  }

  requestFocus(): void {
    if (!this.active()) return;
    this.expand();
    this.pendingFocus.set(true);
  }

  finishFocus(): void {
    this.pendingFocus.set(false);
  }

  finishAnimation(): void {
    this.launchOrigin.set(null);
  }

  private async deliver(message: string): Promise<void> {
    await this.ensureSession();
    const context = this.context();
    if (!context) return;
    if (this.conversation.sessionId() === null) {
      // Starting failed (no access, hub unreachable): keep the message so it isn't lost.
      this.failed.set(true);
      this.update({ draft: message });
      return;
    }
    await this.conversation.sendMessage(message, {
      groupId: context.groupId,
      productContextId: context.productContextId,
      pageKey: context.pageKey,
      visiblePageKeys: context.visiblePageKeys ?? null,
    });
  }

  /** Starts or resumes the server session once; resuming loads the history. */
  private ensureSession(): Promise<void> {
    if (this.conversation.sessionId() !== null) return Promise.resolve();
    this.sessionStart ??= (async () => {
      this.starting.set(true);
      try {
        await this.reconnecting;
        await this.conversation.connect();
        if (this.conversation.isConnected()) this.connectedCompanyId = this.context()?.companyId ?? null;
        await this.conversation.startSession(COACH_AGENT_KEY, null);
      } finally {
        this.starting.set(false);
        this.sessionStart = null;
      }
    })();
    return this.sessionStart;
  }

  private update(change: Partial<CoachState>): void {
    const context = this.context();
    if (!context) return;
    this.state.update((state) => ({ ...state, ...change }));
    try {
      this.document.defaultView?.sessionStorage.setItem(this.storageKey(context), JSON.stringify(this.state()));
    } catch {
      return;
    }
  }

  private restoreState(context: CoachContext | null): void {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (!storage) return;
      const current = context ? this.storageKey(context) : null;
      for (const key of Object.keys(storage)) {
        if (key.startsWith('insight.coach.') && key !== current) storage.removeItem(key);
      }
      if (!current) return;
      const saved = storage.getItem(current);
      if (!saved) return;
      const parsed: unknown = JSON.parse(saved);
      // A reload is a fresh start of the page: an open conversation comes back minimized,
      // so it doesn't cover the page, but stays one click away.
      if (this.isState(parsed)) {
        this.state.set({
          ...parsed,
          minimized: parsed.active || parsed.minimized,
          // Saved before the bottom dock had a height.
          dockSize: { ...parsed.dockSize, bottomHeight: parsed.dockSize.bottomHeight ?? null },
          // Saved before the welcome was remembered.
          welcomed: parsed.welcomed === true,
        });
      }
    } catch {
      this.state.set(emptyState());
    }
  }

  private storageKey(context: CoachContext): string {
    return `${STORAGE_PREFIX}${context.companyId}:${context.userId}`;
  }

  private isState(value: unknown): value is CoachState {
    if (!value || typeof value !== 'object') return false;
    const state = value as Partial<CoachState>;
    return typeof state.active === 'boolean' && typeof state.minimized === 'boolean' &&
      typeof state.draft === 'string' &&
      (state.layout === null || state.layout === 'right' || state.layout === 'bottom' || state.layout === 'floating') &&
      (state.bounds === null || this.isBounds(state.bounds)) &&
      this.isDockSize(state.dockSize);
  }

  private isBounds(value: unknown): value is CoachBounds {
    if (!value || typeof value !== 'object') return false;
    const bounds = value as Partial<CoachBounds>;
    return [bounds.top, bounds.left, bounds.width, bounds.height].every(
      (dimension) => typeof dimension === 'number' && Number.isFinite(dimension),
    ) && bounds.width! > 0 && bounds.height! > 0;
  }

  private isDockSize(value: unknown): value is CoachDockSize {
    if (!value || typeof value !== 'object') return false;
    const dockSize = value as Partial<CoachDockSize>;
    return [dockSize.rightWidth, dockSize.bottomLeft, dockSize.bottomWidth, dockSize.bottomHeight ?? null].every(
      (dimension) => dimension === null ||
        (typeof dimension === 'number' && Number.isFinite(dimension)),
    );
  }
}
